<template>
    <transition name="update-prompt">
        <div v-if="available && !dismissed" class="update-prompt" role="status">
            <span class="update-text">En ny version av kortspelet finns</span>
            <button type="button" class="btn btn-primary btn-sm" @click="reload">Uppdatera nu</button>
            <button type="button" class="update-close" aria-label="Senare" @click="dismissed = true">✕</button>
        </div>
    </transition>
</template>

<script setup>
// "En ny version finns – Uppdatera nu". Checks when the app starts, when it comes back from the background and every
// half hour while visible. It never reloads by itself: a move being made would be lost. A reload is enough to update:
// index.html is fetched from the network first and the other files have hashed names.
import { ref, onMounted, onUnmounted } from 'vue';
import { CHECK_EVERY_MS, MIN_GAP_MS, fetchDeployedVersion, isUpdate } from '@/helpers/versioncheck.mjs';

const CURRENT_VERSION = process.env.VUE_APP_BUILD_ID;
const enabled = process.env.NODE_ENV === 'production' && !!CURRENT_VERSION;

const available = ref(false);
const dismissed = ref(false);
let lastCheck = 0;
let timer = null;
let stopped = false;

async function check() {
    if (stopped || document.visibilityState !== 'visible')
        return;
    const now = Date.now();
    if (now - lastCheck < MIN_GAP_MS)
        return;
    lastCheck = now;
    if (isUpdate(CURRENT_VERSION, await fetchDeployedVersion()) && !stopped) {
        available.value = true;
        dismissed.value = false; // "Senare" hides it until the next check that still finds the update
    }
}

function onVisible() {
    if (document.visibilityState === 'visible')
        check();
}

function reload() {
    window.location.reload();
}

onMounted(() => {
    if (!enabled)
        return;
    check();
    timer = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('online', onVisible);
});

onUnmounted(() => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onVisible);
    window.removeEventListener('online', onVisible);
});
</script>

<style scoped lang="scss">
.update-prompt {
    position: fixed;
    left: 50%;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    transform: translateX(-50%);
    z-index: 1045; // above the game switcher button, below its overview and the modals
    display: flex;
    align-items: center;
    gap: 10px;
    max-width: calc(100vw - 32px);
    padding: 10px 10px 10px 16px;
    border-radius: 14px;
    background: #2b2233;
    color: #fff;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

.update-text {
    font-size: 0.95em;
}

.update-close {
    border: none;
    background: transparent;
    color: #fff;
    font-size: 16px;
    padding: 4px 6px;
    opacity: 0.8;
}

.update-prompt-enter-active,
.update-prompt-leave-active {
    transition: opacity 0.2s ease;
}

.update-prompt-enter-from,
.update-prompt-leave-to {
    opacity: 0;
}
</style>
